import { ApiActions } from './api-state';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { switchMap, take, map, catchError, withLatestFrom } from 'rxjs/operators';

interface CreatedCachedApiEffectConfig<T, R> {
  actions: ApiActions<T, R>;
  effectFn: (request: T) => Observable<R>;
  cachedObservable: Observable<R>;
}

export abstract class ApiEffects {
  constructor(protected readonly actions$: Actions) {}

  protected createApiEffect<T, R>(actions: ApiActions<T, R>, effectFn: (request: T) => Observable<R>) {
    return createEffect(() => {
      return this.actions$.pipe(
        ofType(actions.start),
        switchMap((action) => {
          return effectFn(action.request).pipe(
            take(1),
            map((response) => actions.success({ response })),
            catchError((error) => of(actions.error({ error })))
          );
        })
      );
    });
  }

  protected createCachedApiEffect<T, R>({ actions, effectFn, cachedObservable }: CreatedCachedApiEffectConfig<T, R>) {
    return createEffect(() => {
      return this.actions$.pipe(
        ofType(actions.start),
        withLatestFrom(cachedObservable),
        switchMap(([action, cachedRes]) => {
          // if state value already exists, don't dispatch again
          if (cachedRes) {
            return of(actions.success({ response: cachedRes }));
          }
          return effectFn(action.request).pipe(
            take(1),
            map((response) => actions.success({ response })),
            catchError((error) => of(actions.error({ error })))
          );
        })
      );
    });
  }
}
